import React, { useEffect, useMemo, useState } from "react";
import API from "../../../services/api";
import SaveStudioAssetButton from "../SaveStudioAssetButton";
import FieldHint from "../FieldHint";
import useI18n from "../../../i18n/useI18n";

import MotionSurface from "../../../motion/studio/MotionSurface";
import ToolResultSkeleton from "../../loaders/ToolResultSkeleton";

import ResultCard from "../../ui/ResultCard";
import ActionRow from "../../ui/ActionRow";
import CopyButton from "../../ui/CopyButton";

import AIActionButton from "../../ui/buttons/AIActionButton";
import UtilityButton from "../../ui/buttons/UtilityButton";

const THEME_KEY = "hookora_theme";

const platformOptions = [
  { value: "instagram", labelKey: "instagram", fallback: "Instagram" },
  { value: "tiktok", labelKey: "tiktok", fallback: "TikTok" },
  { value: "youtube", labelKey: "youtube", fallback: "YouTube" },
  { value: "x", labelKey: "x", fallback: "X" },
  { value: "facebook", labelKey: "facebook", fallback: "Facebook" },
  { value: "linkedin", labelKey: "linkedin", fallback: "LinkedIn" },
  { value: "general", labelKey: "general", fallback: "General" },
];

const outputTypeOptions = [
  {
    value: "educational_breakdown",
    labelKey: "educationalBreakdown",
    fallback: "Educational Breakdown",
  },
  { value: "mistake_series", labelKey: "mistakeSeries", fallback: "Mistake Series" },
  { value: "framework_series", labelKey: "frameworkSeries", fallback: "Framework Series" },
];

const toneOptions = [
  { value: "Educational", labelKey: "educational", fallback: "Educational" },
  { value: "Bold", labelKey: "bold", fallback: "Bold" },
  { value: "Conversational", labelKey: "conversational", fallback: "Conversational" },
  { value: "Professional", labelKey: "professional", fallback: "Professional" },
  { value: "Persuasive", labelKey: "persuasive", fallback: "Persuasive" },
  { value: "Warm", labelKey: "warm", fallback: "Warm" },
  { value: "Confident", labelKey: "confident", fallback: "Confident" },
];

const languageOptions = [
  { value: "en", labelKey: "english", fallback: "English" },
  { value: "es", labelKey: "spanish", fallback: "Spanish" },
  { value: "fr", labelKey: "french", fallback: "French" },
  { value: "pt", labelKey: "portuguese", fallback: "Portuguese" },
];

const getStoredTheme = () => {
  if (typeof window === "undefined") return "light";
  return localStorage.getItem(THEME_KEY) || "light";
};

const emptyForm = {
  audience: "",
  goal: "",
  offer: "",
  painPoint: "",
  desiredOutcome: "",
  angle: "",
  callToAction: "",
  tone: "Educational",
  keywords: "",
  brandVoice: "",
  style: "educational_breakdown",
  avoidPhrases: "",
  extraContext: "",
  language: "en",
};

const CarouselGeneratorTool = ({
  sharedTopic,
  setSharedTopic,
  sharedPlatform,
  setSharedPlatform,
}) => {
  const { t } = useI18n();

  const [theme, setTheme] = useState(getStoredTheme());
  const [form, setForm] = useState(emptyForm);
  const [carousels, setCarousels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [copiedIndex, setCopiedIndex] = useState(null);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const syncTheme = () => {
      setTheme(localStorage.getItem(THEME_KEY) || "light");
    };

    window.addEventListener("storage", syncTheme);
    window.addEventListener("hookora-preferences-updated", syncTheme);

    return () => {
      window.removeEventListener("storage", syncTheme);
      window.removeEventListener("hookora-preferences-updated", syncTheme);
    };
  }, []);

  const isDark = theme === "dark";

  const colors = useMemo(
    () => ({
      panelBg: isDark ? "rgba(15,23,42,0.72)" : "#ffffff",
      panelBorder: isDark ? "rgba(148,163,184,0.18)" : "#e5e7eb",
      panelShadow: isDark
        ? "0 14px 40px rgba(2, 6, 23, 0.30)"
        : "0 10px 30px rgba(15, 23, 42, 0.06)",
      inputBg: isDark ? "#0f172a" : "#ffffff",
      inputBorder: isDark ? "rgba(148,163,184,0.24)" : "#d1d5db",
      inputText: isDark ? "#f8fafc" : "#111827",
      title: isDark ? "#f8fafc" : "#0f172a",
      text: isDark ? "#e2e8f0" : "#111827",
      muted: isDark ? "#94a3b8" : "#64748b",
      subCardBg: isDark ? "rgba(15,23,42,0.88)" : "#f8fafc",
      subCardBorder: isDark ? "rgba(148,163,184,0.14)" : "#e5e7eb",
      chipBg: isDark ? "rgba(30,41,59,0.95)" : "#eef2ff",
      chipText: isDark ? "#cbd5e1" : "#4338ca",
      buttonBg: isDark ? "#e2e8f0" : "#111827",
      buttonText: isDark ? "#020617" : "#ffffff",
      secondaryButtonBg: isDark ? "#1e293b" : "#e5e7eb",
      secondaryButtonText: isDark ? "#f8fafc" : "#111827",
      errorBg: isDark ? "rgba(127,29,29,0.22)" : "#fef2f2",
      errorBorder: isDark ? "rgba(248,113,113,0.32)" : "#fecaca",
      errorText: isDark ? "#fecaca" : "#b91c1c",
      successBg: "#16a34a",
      slideBg: isDark ? "rgba(37,99,235,0.12)" : "#eff6ff",
      slideBorder: isDark ? "rgba(96,165,250,0.22)" : "#bfdbfe",
      slideNumberBg: isDark ? "rgba(59,130,246,0.18)" : "#dbeafe",
      slideNumberText: isDark ? "#bfdbfe" : "#1d4ed8",
    }),
    [isDark]
  );

  const inputStyle = {
    width: "100%",
    padding: "11px 12px",
    borderRadius: "12px",
    border: `1px solid ${colors.inputBorder}`,
    background: colors.inputBg,
    color: colors.inputText,
    boxSizing: "border-box",
    outline: "none",
  };

  const labelStyle = {
    display: "block",
    marginBottom: "6px",
    color: colors.text,
    fontWeight: "700",
    fontSize: "14px",
  };

  const helperStyle = {
    color: colors.muted,
    fontSize: "12px",
    lineHeight: "1.5",
    marginTop: "6px",
  };

  const handleChange = (field, value) => {
    setForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const buildCarouselText = (item) => {
    const parts = [
      item.hook_slide
        ? `${t("studioTools.carouselGeneratorTool.resultLabels.hookSlide")}: ${item.hook_slide}`
        : "",
      ...(Array.isArray(item.slides)
        ? item.slides.map(
            (slide, idx) =>
              `${t("studioTools.carouselGeneratorTool.resultLabels.slide")} ${idx + 2}: ${slide}`
          )
        : []),
      item.closing_slide
        ? `${t("studioTools.carouselGeneratorTool.resultLabels.closingSlide")}: ${item.closing_slide}`
        : "",
      item.caption
        ? `${t("studioTools.carouselGeneratorTool.resultLabels.caption")}: ${item.caption}`
        : "",
    ].filter(Boolean);

    return parts.join("\n\n");
  };

  const copyText = async (text, index) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedIndex(index);
      setTimeout(() => setCopiedIndex(null), 1800);
    } catch (err) {
      console.error("Failed to copy carousel:", err);
      setError(t("studioTools.carouselGeneratorTool.messages.copyFailed"));
    }
  };

  const handleGenerate = async () => {
    if (!sharedTopic.trim()) {
      setError(t("studioTools.carouselGeneratorTool.messages.topicRequired"));
      return;
    }

    try {
      setLoading(true);
      setError("");
      setCarousels([]);

      const response = await API.post("/studio/carousels", {
        topic: sharedTopic,
        platform: sharedPlatform || "Instagram",
        audience: form.audience,
        goal: form.goal,
        offer: form.offer,
        pain_point: form.painPoint,
        desired_outcome: form.desiredOutcome,
        angle: form.angle,
        call_to_action: form.callToAction,
        tone: form.tone,
        keywords: form.keywords,
        brand_voice: form.brandVoice,
        style: form.style,
        avoid_phrases: form.avoidPhrases,
        extra_context: form.extraContext,
        language: form.language,
      });

      setCarousels(response?.data?.carousels || []);
    } catch (err) {
      console.error("Failed to generate carousels:", err);
      setError(
        err?.response?.data?.detail ||
          t("studioTools.carouselGeneratorTool.messages.generateFailed")
      );
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setForm(emptyForm);
    setCarousels([]);
    setError("");
    setCopiedIndex(null);
  };

  return (
    <MotionSurface>
    <div
      style={{
        display: "grid",
        gap: "18px",
      }}
    >
      <div
        style={{
          padding: "18px",
          borderRadius: "18px",
          border: `1px solid ${colors.panelBorder}`,
          background: colors.panelBg,
          boxShadow: colors.panelShadow,
        }}
      >
        <div style={{ marginBottom: "18px" }}>
          <h2
            style={{
              margin: 0,
              color: colors.title,
              fontSize: "22px",
              lineHeight: "1.15",
            }}
          >
            {t("studioTools.carouselGeneratorTool.title")}
          </h2>
          <p
            style={{
              margin: "8px 0 0",
              color: colors.muted,
              lineHeight: "1.6",
              fontSize: "14px",
            }}
          >
            {t("studioTools.carouselGeneratorTool.description")}
          </p>
        </div>

        {error ? (
          <div
            style={{
              marginBottom: "16px",
              padding: "12px 14px",
              borderRadius: "12px",
              border: `1px solid ${colors.errorBorder}`,
              background: colors.errorBg,
              color: colors.errorText,
              lineHeight: "1.55",
              fontSize: "14px",
            }}
          >
            {error}
          </div>
        ) : null}

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
            gap: "14px",
            marginBottom: "14px",
          }}
        >
          <div>
            <label style={labelStyle}>{t("studioTools.carouselGeneratorTool.fields.topic.label")}</label>
            <input
              type="text"
              value={sharedTopic}
              onChange={(e) => setSharedTopic(e.target.value)}
              placeholder={t("studioTools.carouselGeneratorTool.fields.topic.placeholder")}
              style={inputStyle}
            />
            <div style={helperStyle}>
              {t("studioTools.carouselGeneratorTool.fields.topic.hint")}
            </div>
          </div>

          <div>
            <label style={labelStyle}>
              {t("studioTools.carouselGeneratorTool.fields.platform.label")}
            </label>
            <select
              value={sharedPlatform || "instagram"}
              onChange={(e) => setSharedPlatform(e.target.value)}
              style={inputStyle}
            >
              {platformOptions.map((item) => (
                <option key={item.value} value={item.value}>
                  {t(
                    `studioTools.carouselGeneratorTool.platformOptions.${item.labelKey}`,
                    item.fallback
                  )}
                </option>
              ))}
            </select>
            <div style={helperStyle}>
              {t("studioTools.carouselGeneratorTool.fields.platform.hint")}
            </div>
          </div>

          <div>
            <label style={labelStyle}>{t("studioTools.carouselGeneratorTool.fields.tone.label")}</label>
            <select
              value={form.tone}
              onChange={(e) => handleChange("tone", e.target.value)}
              style={inputStyle}
            >
              {toneOptions.map((tone) => (
                <option key={tone.value} value={tone.value}>
                  {t(`studioTools.carouselGeneratorTool.toneOptions.${tone.labelKey}`, tone.fallback)}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label style={labelStyle}>{t("studioTools.carouselGeneratorTool.fields.style.label")}</label>
            <select
              value={form.style}
              onChange={(e) => handleChange("style", e.target.value)}
              style={inputStyle}
            >
              {outputTypeOptions.map((item) => (
                <option key={item.value} value={item.value}>
                  {t(
                    `studioTools.carouselGeneratorTool.styleOptions.${item.labelKey}`,
                    item.fallback
                  )}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label style={labelStyle}>
              {t("studioTools.carouselGeneratorTool.fields.language.label")}
            </label>
            <select
              value={form.language}
              onChange={(e) => handleChange("language", e.target.value)}
              style={inputStyle}
            >
              {languageOptions.map((item) => (
                <option key={item.value} value={item.value}>
                  {t(
                    `studioTools.carouselGeneratorTool.languageOptions.${item.labelKey}`,
                    item.fallback
                  )}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
            gap: "14px",
            marginBottom: "14px",
          }}
        >
          <div>
            <label style={labelStyle}>
              {t("studioTools.carouselGeneratorTool.fields.audience.label")}
            </label>
            <input
              type="text"
              value={form.audience}
              onChange={(e) => handleChange("audience", e.target.value)}
              placeholder={t("studioTools.carouselGeneratorTool.fields.audience.placeholder")}
              style={inputStyle}
            />
          </div>

          <div>
            <label style={labelStyle}>{t("studioTools.carouselGeneratorTool.fields.goal.label")}</label>
            <input
              type="text"
              value={form.goal}
              onChange={(e) => handleChange("goal", e.target.value)}
              placeholder={t("studioTools.carouselGeneratorTool.fields.goal.placeholder")}
              style={inputStyle}
            />
          </div>

          <div>
            <label style={labelStyle}>{t("studioTools.carouselGeneratorTool.fields.offer.label")}</label>
            <input
              type="text"
              value={form.offer}
              onChange={(e) => handleChange("offer", e.target.value)}
              placeholder={t("studioTools.carouselGeneratorTool.fields.offer.placeholder")}
              style={inputStyle}
            />
          </div>

          <div>
            <label style={labelStyle}>
              {t("studioTools.carouselGeneratorTool.fields.painPoint.label")}
            </label>
            <input
              type="text"
              value={form.painPoint}
              onChange={(e) => handleChange("painPoint", e.target.value)}
              placeholder={t("studioTools.carouselGeneratorTool.fields.painPoint.placeholder")}
              style={inputStyle}
            />
          </div>

          <div>
            <label style={labelStyle}>
              {t("studioTools.carouselGeneratorTool.fields.desiredOutcome.label")}
            </label>
            <input
              type="text"
              value={form.desiredOutcome}
              onChange={(e) => handleChange("desiredOutcome", e.target.value)}
              placeholder={t("studioTools.carouselGeneratorTool.fields.desiredOutcome.placeholder")}
              style={inputStyle}
            />
          </div>

          <div>
            <label style={labelStyle}>{t("studioTools.carouselGeneratorTool.fields.angle.label")}</label>
            <input
              type="text"
              value={form.angle}
              onChange={(e) => handleChange("angle", e.target.value)}
              placeholder={t("studioTools.carouselGeneratorTool.fields.angle.placeholder")}
              style={inputStyle}
            />
          </div>

          <div>
            <label style={labelStyle}>
              {t("studioTools.carouselGeneratorTool.fields.callToAction.label")}
            </label>
            <input
              type="text"
              value={form.callToAction}
              onChange={(e) => handleChange("callToAction", e.target.value)}
              placeholder={t("studioTools.carouselGeneratorTool.fields.callToAction.placeholder")}
              style={inputStyle}
            />
          </div>

          <div>
            <label style={labelStyle}>
              {t("studioTools.carouselGeneratorTool.fields.brandVoice.label")}
            </label>
            <input
              type="text"
              value={form.brandVoice}
              onChange={(e) => handleChange("brandVoice", e.target.value)}
              placeholder={t("studioTools.carouselGeneratorTool.fields.brandVoice.placeholder")}
              style={inputStyle}
            />
          </div>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
            gap: "14px",
            marginBottom: "14px",
          }}
        >
          <div>
            <label style={labelStyle}>
              {t("studioTools.carouselGeneratorTool.fields.keywords.label")}
            </label>
            <input
              type="text"
              value={form.keywords}
              onChange={(e) => handleChange("keywords", e.target.value)}
              placeholder={t("studioTools.carouselGeneratorTool.fields.keywords.placeholder")}
              style={inputStyle}
            />
          </div>

          <div>
            <label style={labelStyle}>
              {t("studioTools.carouselGeneratorTool.fields.avoidPhrases.label")}
            </label>
            <input
              type="text"
              value={form.avoidPhrases}
              onChange={(e) => handleChange("avoidPhrases", e.target.value)}
              placeholder={t("studioTools.carouselGeneratorTool.fields.avoidPhrases.placeholder")}
              style={inputStyle}
            />
          </div>
        </div>

        <div style={{ marginBottom: "16px" }}>
          <label style={labelStyle}>
            {t("studioTools.carouselGeneratorTool.fields.extraContext.label")}
          </label>
          <textarea
            value={form.extraContext}
            onChange={(e) => handleChange("extraContext", e.target.value)}
            placeholder={t("studioTools.carouselGeneratorTool.fields.extraContext.placeholder")}
            rows={4}
            style={{
              ...inputStyle,
              resize: "vertical",
            }}
          />
        </div>

        <ActionRow>
          <AIActionButton isDark={isDark}
            type="button"
            onClick={handleGenerate}
            disabled={loading}
          >
            {loading
              ? t("studioTools.carouselGeneratorTool.actions.generating")
              : t("studioTools.carouselGeneratorTool.actions.generate")}
          </AIActionButton>

          <UtilityButton isDark={isDark}
            type="button"
            onClick={handleReset}
            disabled={loading}
          >
            {t("studioTools.carouselGeneratorTool.actions.reset")}
          </UtilityButton>
        </ActionRow>
      </div>

      <div
        style={{
          padding: "18px",
          borderRadius: "18px",
          border: `1px solid ${colors.panelBorder}`,
          background: colors.panelBg,
          boxShadow: colors.panelShadow,
        }}
      >
        <div style={{ marginBottom: "14px" }}>
          <h3
            style={{
              margin: 0,
              color: colors.title,
              fontSize: "18px",
            }}
          >
            {t("studioTools.carouselGeneratorTool.results.title")}
          </h3>
          <p
            style={{
              margin: "8px 0 0",
              color: colors.muted,
              lineHeight: "1.6",
              fontSize: "14px",
            }}
          >
            {t("studioTools.carouselGeneratorTool.results.description")}
          </p>
        </div>

        {loading ? (
          <ToolResultSkeleton isDark={isDark} count={3} />
        ) : carousels.length === 0 ? (
          <div
            style={{
              padding: "16px",
              borderRadius: "14px",
              border: `1px solid ${colors.subCardBorder}`,
              background: colors.subCardBg,
              color: colors.muted,
              lineHeight: "1.6",
              fontSize: "14px",
            }}
          >
            {t("studioTools.carouselGeneratorTool.results.empty")}
          </div>
        ) : (
          <div
            style={{
              display: "grid",
              gap: "14px",
            }}
          >
            {carousels.map((item, index) => {
              const fullText = buildCarouselText(item);

              return (
                <ResultCard
                  key={`${item.title || "carousel"}-${index}`}
                  colors={{
                    border: colors.subCardBorder,
                    cardBgStrong: colors.subCardBg,
                    shadowSoft: colors.panelShadow,
                  }}
                  index={index}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      gap: "12px",
                      alignItems: "flex-start",
                      flexWrap: "wrap",
                      marginBottom: "12px",
                    }}
                  >
                    <div>
                      <h4
                        style={{
                          margin: 0,
                          color: colors.title,
                          fontSize: "16px",
                          lineHeight: "1.3",
                        }}
                      >
                        {item.title || `${t("studioTools.carouselGeneratorTool.resultLabels.carousel")} ${index + 1}`}
                      </h4>

                      <div
                        style={{
                          display: "flex",
                          gap: "8px",
                          flexWrap: "wrap",
                          marginTop: "8px",
                        }}
                      >
                        {item.style ? (
                          <span
                            style={{
                              padding: "4px 9px",
                              borderRadius: "999px",
                              background: colors.chipBg,
                              color: colors.chipText,
                              fontSize: "12px",
                              fontWeight: "700",
                            }}
                          >
                            {t(
                              `studioTools.carouselGeneratorTool.styleOptions.${
                                outputTypeOptions.find((option) => option.value === item.style)?.labelKey ||
                                "educationalBreakdown"
                              }`,
                              item.style.replace(/_/g, " ")
                            )}
                          </span>
                        ) : null}

                        {item.label ? (
                          <span
                            style={{
                              padding: "4px 9px",
                              borderRadius: "999px",
                              background: colors.chipBg,
                              color: colors.chipText,
                              fontSize: "12px",
                              fontWeight: "700",
                            }}
                          >
                            {item.label}
                          </span>
                        ) : null}

                        {typeof item.score === "number" ? (
                          <span
                            style={{
                              padding: "4px 9px",
                              borderRadius: "999px",
                              background: colors.chipBg,
                              color: colors.chipText,
                              fontSize: "12px",
                              fontWeight: "700",
                            }}
                          >
                            {t("studioTools.carouselGeneratorTool.resultLabels.score")}: {item.score}
                          </span>
                        ) : null}
                      </div>
                    </div>
                  </div>

                  <div
                    style={{
                      display: "grid",
                      gap: "10px",
                    }}
                  >
                    <div
                      style={{
                        padding: "12px",
                        borderRadius: "12px",
                        background: colors.slideBg,
                        border: `1px solid ${colors.slideBorder}`,
                      }}
                    >
                      <div
                        style={{
                          display: "inline-flex",
                          padding: "4px 8px",
                          borderRadius: "999px",
                          background: colors.slideNumberBg,
                          color: colors.slideNumberText,
                          fontSize: "12px",
                          fontWeight: "700",
                          marginBottom: "8px",
                        }}
                      >
                        {t("studioTools.carouselGeneratorTool.resultLabels.slide")} 1
                      </div>
                      <div
                        style={{
                          color: colors.text,
                          fontWeight: "700",
                          lineHeight: "1.6",
                          fontSize: "15px",
                        }}
                      >
                        {item.hook_slide}
                      </div>
                    </div>

                    {Array.isArray(item.slides) &&
                      item.slides.map((slide, slideIndex) => (
                        <div
                          key={`${index}-${slideIndex}`}
                          style={{
                            padding: "12px",
                            borderRadius: "12px",
                            background: colors.slideBg,
                            border: `1px solid ${colors.slideBorder}`,
                          }}
                        >
                          <div
                            style={{
                              display: "inline-flex",
                              padding: "4px 8px",
                              borderRadius: "999px",
                              background: colors.slideNumberBg,
                              color: colors.slideNumberText,
                              fontSize: "12px",
                              fontWeight: "700",
                              marginBottom: "8px",
                            }}
                          >
                            {t("studioTools.carouselGeneratorTool.resultLabels.slide")} {slideIndex + 2}
                          </div>
                          <div
                            style={{
                              color: colors.text,
                              lineHeight: "1.6",
                              fontSize: "14px",
                            }}
                          >
                            {slide}
                          </div>
                        </div>
                      ))}

                    {item.closing_slide ? (
                      <div
                        style={{
                          padding: "12px",
                          borderRadius: "12px",
                          background: colors.slideBg,
                          border: `1px solid ${colors.slideBorder}`,
                        }}
                      >
                        <div
                          style={{
                            display: "inline-flex",
                            padding: "4px 8px",
                            borderRadius: "999px",
                            background: colors.slideNumberBg,
                            color: colors.slideNumberText,
                            fontSize: "12px",
                            fontWeight: "700",
                            marginBottom: "8px",
                          }}
                        >
                          {t("studioTools.carouselGeneratorTool.resultLabels.closingSlide")}
                        </div>
                        <div
                          style={{
                            color: colors.text,
                            fontWeight: "700",
                            lineHeight: "1.6",
                            fontSize: "14px",
                          }}
                        >
                          {item.closing_slide}
                        </div>
                      </div>
                    ) : null}

                    {item.caption ? (
                      <div>
                        <div style={labelStyle}>
                          {t("studioTools.carouselGeneratorTool.resultLabels.caption")}
                        </div>
                        <div
                          style={{
                            color: colors.text,
                            whiteSpace: "pre-wrap",
                            lineHeight: "1.7",
                            fontSize: "14px",
                          }}
                        >
                          {item.caption}
                        </div>
                      </div>
                    ) : null}
                  </div>

                  <ActionRow style={{ marginTop: "14px" }}>
                    <CopyButton
                      copied={copiedIndex === index}
                      type="button"
                      onClick={() => copyText(fullText, index)}
                    >
                      {copiedIndex === index
                        ? t("studioTools.carouselGeneratorTool.actions.copied")
                        : t("studioTools.carouselGeneratorTool.actions.copy")}
                    </CopyButton>

                    <SaveStudioAssetButton
                      toolType="carousel_generator"
                      title={item.title || `${t("studioTools.carouselGeneratorTool.resultLabels.carousel")} ${index + 1}`}
                      topic={sharedTopic || item.title || t("studioTools.carouselGeneratorTool.resultLabels.carousel")}
                      platform={sharedPlatform || "Instagram"}
                      content={fullText}
                      meta={{
                        source_tool: "carousel_generator",
                        style: item.style || "",
                        hook_slide: item.hook_slide || "",
                        closing_slide: item.closing_slide || "",
                        caption: item.caption || "",
                        score: item.score,
                        label: item.label,
                        slides_count: Array.isArray(item.slides)
                          ? item.slides.length + 2
                          : 0,
                      }}
                    />
                  </ActionRow>
                </ResultCard>
              );
            })}
          </div>
        )}
      </div>
    </div>
    </MotionSurface>
  );
};

export default CarouselGeneratorTool;